import type { ReactNode } from 'react';
import type { OverviewResponse } from '@/api/types';
import { RANGES } from '@/hooks/useOverview';

interface RangeBarProps {
  days: number;
  setDays: (value: number) => void;
  from: string;
  to: string;
  data?: OverviewResponse;
  /** Supplied by useOverview; when present the bar offers explicit date inputs. */
  setRange?: (from: string | null, to: string | null) => void;
  isCustomRange?: boolean;
  /** Rendered at the end of the control row — the export button goes here. */
  actions?: ReactNode;
}

/**
 * Filter row + data-quality notes.
 *
 * The notes are deliberately INLINE TEXT, not callout cards. They used to be three stacked
 * callouts, which — sitting directly under the alerts strip that already reports the same gaps —
 * meant the reader met six or seven warning blocks before the first number. The facts still have
 * to be stated (a missed day and a genuinely quiet day both plot as zero, and for Clarity the
 * missed one is unrecoverable), but stating them quietly keeps them readable.
 */
export function RangeBar({
  days, setDays, from, to, data, setRange, isCustomRange = false, actions,
}: RangeBarProps) {
  const today = new Date().toISOString().slice(0, 10);
  const gaps = data?.missing_dates ?? [];
  const provisional = data?.provisional_dates ?? [];
  const rangeDays = dayCount(from, to);
  const allMissing = gaps.length >= rangeDays && rangeDays > 0;

  return (
    <div className="rangebar">
      <div className="rangebar-controls">
        {RANGES.map((range) => (
          <button
            key={range.days}
            // A preset is only "active" when no explicit range overrides it, or two controls
            // would both appear selected while only one is in effect.
            className={`btn ${!isCustomRange && days === range.days ? 'btn-primary' : 'btn-ghost'}`}
            onClick={() => setDays(range.days)}
          >
            {range.label}
          </button>
        ))}

        {setRange && (
          <span className="rangebar-dates">
            <input
              type="date"
              className="input"
              style={{ width: 'auto' }}
              value={from}
              max={to || today}
              onChange={(e) => setRange(e.target.value, to)}
              aria-label="From date"
            />
            <span className="t-label subtle">→</span>
            <input
              type="date"
              className="input"
              style={{ width: 'auto' }}
              value={to}
              min={from}
              max={today}
              onChange={(e) => setRange(from, e.target.value)}
              aria-label="To date"
            />
            {isCustomRange && (
              <button className="btn btn-ghost" onClick={() => setRange(null, null)}>
                Reset
              </button>
            )}
          </span>
        )}

        {!setRange && <span className="t-label subtle">{from} → {to}</span>}

        {data && data.providers_included.length > 0 && (
          <span className="t-label subtle">· {data.providers_included.join(' + ')}</span>
        )}

        {actions && <span className="rangebar-actions">{actions}</span>}
      </div>

      {data && (gaps.length > 0 || provisional.length > 0) && (
        <div className="rangebar-notes t-label">
          {gaps.length > 0 && (
            <span className={allMissing ? 'note-error' : 'note-warning'}>
              {allMissing
                ? 'No data ingested for this range yet'
                : `${gaps.length} of ${rangeDays} days missing — shown hatched, not as zero`}
            </span>
          )}
          {provisional.length > 0 && (
            <span className="note-info">
              {provisional.join(', ')} still updating (refreshes every 3h)
            </span>
          )}
        </div>
      )}
    </div>
  );
}

function dayCount(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00Z`);
  const b = Date.parse(`${to}T00:00:00Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / 86_400_000) + 1;
}
